import { useEffect, useState, useRef } from "react";
import { api, formatApiError, img } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { useNavigate } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import { Star, Loader2, ShieldAlert, CheckCircle2, Clock } from "lucide-react";
import { motion } from "framer-motion";

const DURATION = 30;

export default function DailyReview() {
  const nav = useNavigate();
  const { refresh } = useAuth();
  const [status, setStatus] = useState(null);
  const [artwork, setArtwork] = useState(null);
  const [phase, setPhase] = useState("idle"); // idle | timing | rate | done
  const [seconds, setSeconds] = useState(DURATION);
  const [rating, setRating] = useState(0);
  const [hover, setHover] = useState(0);
  const [submitting, setSubmitting] = useState(false);
  const [disqualified, setDisqualified] = useState(false);
  const timerRef = useRef(null);

  useEffect(() => { loadStatus(); }, []);

  const loadStatus = async () => {
    const { data } = await api.get("/reviews/status");
    setStatus(data);
  };

  const startReview = async () => {
    try {
      const { data } = await api.get("/reviews/assigned");
      setArtwork(data.artwork);
      setPhase("timing");
      setSeconds(DURATION);
      setDisqualified(false);
    } catch (e) { toast.error(formatApiError(e)); }
  };

  // countdown
  useEffect(() => {
    if (phase !== "timing") return;
    timerRef.current = setInterval(() => {
      setSeconds((s) => {
        if (s <= 1) { clearInterval(timerRef.current); setPhase("rate"); return 0; }
        return s - 1;
      });
    }, 1000);
    return () => clearInterval(timerRef.current);
  }, [phase]);

  // disqualify if user leaves the page during timing
  useEffect(() => {
    const onHide = () => {
      if (phase === "timing" && document.hidden) {
        clearInterval(timerRef.current);
        setPhase("idle");
        setArtwork(null);
        setDisqualified(true);
      }
    };
    document.addEventListener("visibilitychange", onHide);
    return () => document.removeEventListener("visibilitychange", onHide);
  }, [phase]);

  const submit = async () => {
    if (rating < 1) return;
    setSubmitting(true);
    try {
      const { data } = await api.post("/reviews/submit", { artwork_id: artwork.artwork_id, rating });
      await refresh();
      await loadStatus();
      setPhase("done");
      toast.success(`+₦${data.reward} added to your Reward Wallet!`);
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setSubmitting(false); }
  };

  if (!status) return <div className="min-h-[50vh] grid place-items-center"><Loader2 className="h-8 w-8 animate-spin text-gold" /></div>;

  const radius = 70, circ = 2 * Math.PI * radius;
  const progress = (DURATION - seconds) / DURATION;

  return (
    <div className="max-w-xl mx-auto space-y-6">
      <div className="text-center">
        <h1 className="font-heading text-3xl md:text-4xl font-bold">Daily Review</h1>
        <p className="text-sm text-muted-foreground mt-1">Earn ₦{status.reward_per_review} per review • {status.remaining} of {status.max_daily} remaining today</p>
      </div>

      {disqualified && (
        <div className="bg-destructive/10 border border-destructive/30 rounded-2xl p-4 flex items-center gap-3">
          <ShieldAlert className="h-5 w-5 text-destructive shrink-0" />
          <p className="text-sm">You left the page before the timer finished, so this review didn't qualify. Please try again and stay on the page.</p>
        </div>
      )}

      {status.remaining === 0 && phase !== "done" ? (
        <Empty title="You're all caught up!" desc="You've completed today's reviews. Come back tomorrow for more rewards." cta="Back to Dashboard" onClick={() => nav("/dashboard")} icon={CheckCircle2} />
      ) : phase === "idle" ? (
        <div className="bg-card border border-border rounded-3xl p-8 text-center">
          <div className="h-14 w-14 rounded-2xl navy-gradient grid place-items-center mx-auto"><Clock className="h-7 w-7 text-white" /></div>
          <h3 className="font-heading text-xl font-bold mt-4">Ready to review?</h3>
          <p className="text-sm text-muted-foreground mt-1">You'll view an artwork for 30 seconds, then rate it 1–5 stars. Stay on the page to earn your reward.</p>
          <Button onClick={startReview} data-testid="start-review-btn" className="rounded-full h-12 px-8 mt-6 navy-gradient text-white font-semibold">Start 30-Second Review</Button>
        </div>
      ) : phase === "done" ? (
        <motion.div initial={{ scale: 0.9, opacity: 0 }} animate={{ scale: 1, opacity: 1 }} className="bg-card border border-border rounded-3xl p-8 text-center">
          <CheckCircle2 className="h-16 w-16 text-gold mx-auto" />
          <h3 className="font-heading text-2xl font-bold mt-4">Review complete!</h3>
          <p className="text-sm text-muted-foreground mt-1">₦{status.reward_per_review} credited to your Reward Wallet.</p>
          <div className="flex gap-3 justify-center mt-6">
            {status.remaining > 0 && <Button onClick={() => { setPhase("idle"); setRating(0); }} data-testid="review-again-btn" className="rounded-full navy-gradient text-white font-semibold">Review Again</Button>}
            <Button variant="outline" className="rounded-full" onClick={() => nav("/dashboard")}>Done</Button>
          </div>
        </motion.div>
      ) : (
        <div className="bg-card border border-border rounded-3xl overflow-hidden" data-testid="review-active">
          <div className="bg-muted overflow-hidden flex items-center justify-center py-4"><img src={img(artwork?.image)} alt={artwork?.title} className="max-h-[440px] w-auto max-w-full object-contain aspect-[3/4]" /></div>
          <div className="p-6 text-center">
            <h3 className="font-heading text-xl font-bold">{artwork?.title}</h3>
            <p className="text-sm text-muted-foreground">{artwork?.artist}</p>

            {phase === "timing" ? (
              <div className="mt-6 flex flex-col items-center">
                <div className="relative h-40 w-40">
                  <svg className="h-40 w-40 -rotate-90" viewBox="0 0 160 160">
                    <circle cx="80" cy="80" r={radius} fill="none" stroke="hsl(var(--muted))" strokeWidth="10" />
                    <motion.circle cx="80" cy="80" r={radius} fill="none" stroke="hsl(var(--gold))" strokeWidth="10" strokeLinecap="round"
                      strokeDasharray={circ} strokeDashoffset={circ * (1 - progress)} />
                  </svg>
                  <div className="absolute inset-0 grid place-items-center">
                    <span className="font-heading text-4xl font-bold" data-testid="review-timer">{seconds}</span>
                  </div>
                </div>
                <p className="text-sm text-muted-foreground mt-4">Please stay on this page and observe the artwork…</p>
              </div>
            ) : (
              <div className="mt-6">
                <p className="text-sm font-medium">Rate this artwork</p>
                <div className="flex justify-center gap-2 mt-3" data-testid="star-rating">
                  {[1, 2, 3, 4, 5].map((n) => (
                    <button key={n} onMouseEnter={() => setHover(n)} onMouseLeave={() => setHover(0)} onClick={() => setRating(n)} data-testid={`star-${n}`} className="transition-transform hover:scale-110">
                      <Star className={`h-9 w-9 ${(hover || rating) >= n ? "fill-gold text-gold" : "text-muted-foreground"}`} />
                    </button>
                  ))}
                </div>
                <Button onClick={submit} disabled={rating < 1 || submitting} data-testid="submit-review-btn" className="rounded-full h-12 px-8 mt-6 navy-gradient text-white font-semibold">
                  {submitting ? <Loader2 className="h-4 w-4 animate-spin" /> : `Submit & Earn ₦${status.reward_per_review}`}
                </Button>
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
}

function Empty({ title, desc, cta, onClick, icon: Icon = Star }) {
  return (
    <div className="bg-card border border-border rounded-3xl p-12 text-center max-w-xl mx-auto">
      <Icon className="h-10 w-10 text-gold mx-auto" />
      <h3 className="font-heading text-xl font-bold mt-4">{title}</h3>
      <p className="text-sm text-muted-foreground mt-1">{desc}</p>
      <Button onClick={onClick} className="rounded-full mt-5 navy-gradient text-white font-semibold" data-testid="empty-cta-btn">{cta}</Button>
    </div>
  );
}
